import React, { useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    useTheme,
    useMediaQuery,
    Avatar,
    Divider,
    Stack
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Phone as PhoneIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const ThankYouPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Google Ads conversion tracking
    useEffect(() => {
        const script = document.createElement('script');
        script.innerHTML = `gtag('event', 'conversion', {'send_to': 'AW-11171841723/h-DTCNKiqJwZELuNk88p'});`;
        document.head.appendChild(script);

        return () => {
            document.head.removeChild(script);
        };
    }, []);


    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            //   background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
            p: 2
        }}>
            <Box sx={{
                width: isMobile ? '100%' : '600px',
                bgcolor: 'background.paper',
                borderRadius: 4,
                boxShadow: 24,
                p: isMobile ? 3 : 4,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '8px',
                    background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                }
            }}>
                {/* Animated Check Icon */}
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                    <Avatar sx={{
                        width: 80,
                        height: 80,
                        bgcolor: 'success.light',
                        mx: 'auto',
                        mb: 3,
                        boxShadow: `0 0 0 8px ${theme.palette.success.light}40`
                    }}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.dark' }} />
                    </Avatar>
                </motion.div>

                {/* Main Heading with Special Font Style */}
                <Typography
                    variant="h3"
                    component="h3"
                    sx={{
                        fontWeight: 900,
                        mb: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.dark})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontFamily: '"Poppins", "Montserrat", "Helvetica", "Arial", sans-serif',
                        fontSize: isMobile ? '2rem' : '2.8rem',
                        letterSpacing: '-0.8px',
                        lineHeight: 1.3,
                        textAlign: 'center',
                        position: 'relative',
                        display: 'inline-block',
                        transition: 'all 0.3s ease-in-out',
                        '&:after': {
                            content: '""',
                            position: 'absolute',
                            bottom: -10,
                            left: '20%',
                            width: '60%',
                            height: '4px',
                            background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                            borderRadius: 2,
                        },

                    }}
                >
                    Thank you for choosing Podar Juniors!
                </Typography>


                <Typography
                    variant="body1"
                    sx={{
                        color: 'text.secondary',
                        mb: 3,
                        fontSize: isMobile ? '1rem' : '1.1rem'
                    }}
                >
                    Your admission inquiry has been successfully submitted. Our team will contact you
                    within 24 hours to guide you through the enrollment process.
                </Typography>

                <Divider sx={{ my: 3 }} />

                {/* Next Steps */}
                <Stack spacing={2} sx={{ mb: 4, textAlign: 'left' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                        What to expect next:
                    </Typography>
                    {[
                        'Personalized call from our admissions counselor',
                        'Information about our curriculum and facilities',
                        'Invitation for school tour',
                        'Guidance on admission process'
                    ].map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                            <ArrowForwardIcon color="primary" sx={{ mr: 1.5 }} />
                            <Typography variant="body1">{item}</Typography>
                        </Box>
                    ))}
                </Stack>

                {/* Contact Info */}
                <Box sx={{
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    p: 2,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <PhoneIcon color="primary" sx={{ mr: 1.5 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        For immediate assistance: +91 98445 65757
                    </Typography>
                </Box>

                {/* Action Buttons */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="center">
                    <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth={isMobile}
                            sx={{
                                fontWeight: 700,
                                borderRadius: 2,
                                px: 4,
                                bgcolor: 'primary.main',
                                '&:hover': {
                                    bgcolor: 'primary.dark'
                                }
                            }}
                            onClick={() => navigate('/')}
                        >
                            Back to Homepage
                        </Button>
                    </motion.div>

                    {/* <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
            <Button
              variant="outlined"
              size="large"
              fullWidth={isMobile}
              sx={{
                fontWeight: 700,
                borderRadius: 2,
                px: 4,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2
                }
              }}
            >
              Download Brochure
            </Button>
          </motion.div> */}
                </Stack>
            </Box>
        </Box>
    );
};

export default ThankYouPage;